import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { breadcrumbSchema } from "@/lib/seo";

const works = [
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/7e9e1275744e29bcc34a289b292e383f768afd81abe5dd68416a16bdc21a11ac.jpeg", title: "Wedding Sign", slug: "wedding-sign", meta: "Laser Engraved · Walnut", tall: true },
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/75d7f85a3972f4e7bbb873324ae8207b9eca87da99c928c8501005fb4200aa77.jpeg", title: "Topographic Tumbler", slug: "topographic-tumbler", meta: "Fiber Laser · Steel" },
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/80e763a89bfe2b58213b7b5ee16146a6c61eef4768038de2110c28fa3c80bc50.jpeg", title: "Floral Art Tile", slug: "ceramic-art-tile", meta: "UV Printed · Ceramic" },
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/b20765d057399d9b2b4ac9668b32b7b81e2d96b742500b8878da681d63d66043.jpeg", title: "Family Crest Decanter", slug: "crest-decanter", meta: "UV Printed · Crystal", tall: true },
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/0ba2291ca9142cc8ffe3a5117efcac9ec3da5e67ea94d974dca784a0904d4783.jpeg", title: "Compass Journal", slug: "leather-journal", meta: "Laser Engraved · Leather" },
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/d93eb507f7ce3e3d74a04d875593ccd8a8a460a956c63dfe1ac467c3c5cce395.jpeg", title: "Pet Portrait Plaque", slug: "pet-portrait-plaque", meta: "UV Printed · Acrylic", tall: true },
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/d70d662ad8ba7b7e9e3c0a1ddfa8c803224596ef1692b76ced3a53f487c181e9.jpeg", title: "Corporate Awards", meta: "Laser Engraved · Wood & Brass", wide: true },
  { img: "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/cba9eb3c25fd58b678421b67dede43da37930faf8df7962ba680647e8deb65bc.jpeg", title: "Stag Flask", slug: "stainless-flask", meta: "Fiber Laser · Anodised Steel" },
];

export default function Gallery() {
  return (
    <main data-testid="gallery-page" className="pt-40 pb-32 min-h-screen">
      <Seo
        title="Gallery — Laser Engraving & UV Printing Examples | Ember & Steel Studio, Leduc"
        description="Finished laser engraving and UV printing from our Leduc, Alberta studio: topographic tumblers, wedding signs, crest decanters, leather journals, pet portraits and corporate awards for Edmonton-area clients."
        path="/gallery"
        jsonLd={breadcrumbSchema([["Home", "/"], ["Gallery", "/gallery"]])}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">The Work</span>
          <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight">
            Pieces that left the studio
          </h1>
          <p className="text-zinc-400 mt-6 max-w-xl">
            A selection of recent commissions — burned, etched and printed on the xTool F2 Ultra
            and our UV flatbed. Every one started as somebody's idea.
          </p>
          <Link to="/portfolio" data-testid="gallery-portfolio-link" className="link-line inline-block mt-6 font-mono-tech text-xs uppercase tracking-[0.2em] text-[#C87455]">Corporate case studies →</Link>
        </Reveal>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 mt-16 [column-fill:_balance]">
          {works.map((w, i) => (
            <motion.figure
              key={w.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group laser-card break-inside-avoid mb-6 bg-[#0C0C0C] border border-zinc-800/60 hover:border-[#C87455]/40 transition-colors duration-500"
              data-testid={`gallery-item-${i}`}
            >
              <Link to={w.slug ? `/shop/${w.slug}` : "/quote"} data-testid={`gallery-link-${w.slug || "quote"}`} className="block">
                <div className="overflow-hidden relative">
                  <img src={w.img} alt={w.title} loading="lazy" className="card-img w-full object-cover" />
                  <span className="absolute bottom-3 right-3 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-black bg-[#C87455] px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {w.slug ? "Shop this piece →" : "Commission →"}
                  </span>
                </div>
                <figcaption className="px-5 py-4 border-t border-zinc-800/60 flex justify-between items-baseline gap-3">
                  <span className="font-serif-display text-lg text-white group-hover:text-[#C87455] transition-colors duration-300">{w.title}</span>
                  <span className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-zinc-500 text-right shrink-0">{w.meta}</span>
                </figcaption>
              </Link>
            </motion.figure>
          ))}
        </div>

        <Reveal className="text-center mt-24">
          <p className="font-serif-display text-2xl md:text-3xl text-zinc-400 italic">Yours could be next.</p>
          <div className="flex justify-center gap-4 mt-8 flex-wrap">
            <Link to="/quote" data-testid="gallery-quote-btn" className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm inline-flex items-center gap-3">
              Commission a Piece <ArrowRight size={14} />
            </Link>
            <Link to="/shop" data-testid="gallery-shop-btn" className="border border-[#A9B0B7]/50 text-[#A9B0B7] hover:bg-[#A9B0B7] hover:text-black transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm">
              Shop the Collection
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
